(ns leg.pwa
  "Making this window installable: the worker that keeps the shell, and the
  one place where being an app rather than a tab changes what a link does.

  Everything that decides what Leser looks like as an app stands in
  public/manifest.webmanifest and public/sw.js - the browser reads both of
  them itself. What is left for the window to do is to say once that the
  worker exists.

  It is registered and then left alone. A new build brings a new worker
  which takes over as soon as it is installed, and no window is reloaded
  out from under anybody to make that happen: the shell is fetched from the
  network first, so a window that is online is already looking at the
  current one."
  (:require [clojure.string :as str]
            [kc.core :as kc]))

(defn install! []
  (when-let [container (some-> js/navigator .-serviceWorker)]
    (-> (.register container "/sw.js" #js {:scope "/"})
        (.catch (fn [e] (kc/log-error "service worker not registered" e))))))

;;; ------------------------------------------------------------ out of here

(defn- display-mode?
  [mode]
  (boolean (some-> js/window (.matchMedia (str "(display-mode: " mode ")")) .-matches)))

(defn standalone?
  "Whether this is the installed app and not a tab in a browser. Asked of
  the display mode rather than remembered, because the same build is both:
  one window can be installed while another is a tab beside it."
  []
  (boolean (or (display-mode? "standalone")
               (display-mode? "fullscreen")
               (display-mode? "minimal-ui")
               (.-standalone js/navigator))))

(defn- android? []
  (boolean (re-find #"Android" (or (some-> js/navigator .-userAgent) ""))))

(defn intent-href
  "One `https` address as an Android intent, or nil if it cannot be one.

  This is the only way a page has of saying \"not here\": a link, even one
  with `target=_blank`, is handed back to whatever is showing the page, and
  in an installed app that is the app itself - the article opens in a
  browser with no address bar, inside Leser, with the list behind it gone.
  An `intent:` address is handed to Android instead, which opens it the way
  it opens a link from anywhere else: in whichever browser is the one on
  this phone.

  Nothing else is in it - no package, no fallback. Naming a browser would
  be this program deciding which one somebody uses, and the whole point is
  that it does not.

  An address with a fragment is refused rather than mangled: everything
  after the `#` is where the intent's own parameters go, and a link that
  opens the wrong thing would be worse than one that opens here."
  [url]
  (when-let [u (try (js/URL. url) (catch :default _ nil))]
    (let [scheme (str/replace (.-protocol u) ":" "")]
      (when (and (contains? #{"http" "https"} scheme)
                 (str/blank? (.-hash u))
                 (not (str/blank? (.-host u))))
        (str "intent://" (.-host u) (.-pathname u) (.-search u)
             "#Intent;scheme=" scheme
             ";action=android.intent.action.VIEW"
             ";category=android.intent.category.BROWSABLE"
             ";end")))))

(defn leaving?
  "Whether an address leads out of Leser. Compared by origin and not by
  host: this is the same question the browser asks, and a link back into
  the reader itself belongs in the reader."
  [url origin]
  (boolean (when-let [u (try (js/URL. url) (catch :default _ nil))]
             (not= (.-origin u) origin))))

(defn browser-href
  "What a link that leads out of Leser should go to instead, or nil to
  leave it alone.

  Only in the installed app on Android, because that is the only place
  where the question comes up and the only place with an answer to it: a
  tab in a browser already opens a new tab, and iOS has nothing like an
  intent - `target=_blank` there is as far outside as a page can get."
  [url]
  (when (and (standalone?) (android?))
    (intent-href url)))

(defn- plain-click?
  "A press of a link and nothing cleverer. A held key means the person
  asked for something particular to happen, and the answer to that is to
  let the browser do it."
  [^js e]
  (and (zero? (.-button e))
       (not (.-defaultPrevented e))
       (not (.-metaKey e))
       (not (.-ctrlKey e))
       (not (.-shiftKey e))
       (not (.-altKey e))))

(defn install-links!
  "Every link that leads out of Leser leads out of the installed app too.

  One listener on the document, like the keyboard (`leg.keys`), for two
  reasons. The article's body is markup the server wrote and put into the
  page whole (`leg.article/Body`), so there is no component to hang a
  handler on - and a link in a feed's text is the way out a reader presses
  most, now that the headline is the only other one. And the `href` stays
  what it says it is: an intent written into the attribute would be what a long
  press offers to copy, and what somebody sends to a friend.

  A link back into Leser is left alone, and so is everything outside the
  installed app on Android - see `browser-href`."
  []
  (.addEventListener
   js/document "click"
   (fn [^js e]
     (when (plain-click? e)
       (when-let [a (some-> (.-target e) (.closest "a[href]"))]
         (let [url (.-href a)]
           (when (leaving? url (.. js/location -origin))
             (when-let [out (browser-href url)]
               (.preventDefault e)
               ;; The intent goes to Android, which starts the browser and
               ;; leaves this window where it is - `location` and not
               ;; `window.open`, which would be a window this app has no
               ;; use for and would have to close again.
               (set! (.. js/location -href) out)))))))))
